package com.partejar.app.feature.gestantes.presentation

import androidx.compose.animation.*
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.*
import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.*

private sealed interface DeliveryPanel {
    data object Introduction : DeliveryPanel
    data object Form : DeliveryPanel
    data class Summary(val delivery: GestanteDelivery) : DeliveryPanel
}

@Composable
internal fun DeliverySection(detail: GestanteClinicalDetail, saving: Boolean, onSave: (GestanteDeliveryInput) -> Unit) {
    var formOpen by rememberSaveable(detail.id) { mutableStateOf(false) }
    val panel = detail.delivery?.let(DeliveryPanel::Summary)
        ?: if (formOpen) DeliveryPanel.Form else DeliveryPanel.Introduction
    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
        GestanteSectionHeader("Parto", PartejarIcons.Clinical.schedule)
        DeliveryTransition(panel) { target ->
            when (target) {
                DeliveryPanel.Introduction -> DeliveryIntroduction(!saving) { formOpen = true }
                DeliveryPanel.Form -> DeliveryForm(detail, saving, onSave) { formOpen = false }
                is DeliveryPanel.Summary -> DeliveryReadOnly(target.delivery)
            }
        }
    }
}

@Composable
private fun DeliveryIntroduction(enabled: Boolean, onOpen: () -> Unit) {
    val spacing = PartejarTheme.spacing
    PartejarCard(Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
            DeliveryHeader(PartejarIcons.Clinical.schedule, "Registrar parto",
                "Registre os dados do parto para concluir o acompanhamento clínico.")
            PartejarAlert("Esta ação encerra o acompanhamento clínico.", PartejarAlertTone.ATTENTION) {
                Text("Os dados permanecem disponíveis somente para consulta.")
            }
            DeliveryAction("Registrar parto", PartejarIcons.Actions.next, enabled, onClick = onOpen)
        }
    }
}

@Composable
private fun DeliveryForm(
    detail: GestanteClinicalDetail,
    saving: Boolean,
    onSave: (GestanteDeliveryInput) -> Unit,
    onCancel: () -> Unit
) {
    var date by rememberSaveable { mutableStateOf("") }
    var dateError by rememberSaveable { mutableStateOf<String?>(null) }
    var time by rememberSaveable { mutableStateOf("") }
    var location by rememberSaveable(detail.maternity) { mutableStateOf(detail.maternity.orEmpty()) }
    var route by remember { mutableStateOf(GestanteDeliveryRoute.VAGINAL) }
    var maternal by remember { mutableStateOf(GestanteMaternalOutcome.WITHOUT_RECORDED_COMPLICATION) }
    var neonatal by remember { mutableStateOf(GestanteNeonatalOutcome.LIVE_BIRTH) }
    var observation by rememberSaveable { mutableStateOf("") }
    var confirmed by rememberSaveable { mutableStateOf(false) }
    val spacing = PartejarTheme.spacing

    PartejarCard(Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space20)) {
            DeliveryHeader(PartejarIcons.Clinical.schedule, "Registrar parto",
                "Preencha os dados reais do parto antes de encerrar o acompanhamento.")
            PartejarAlert("Esta ação encerra o acompanhamento clínico.", PartejarAlertTone.ATTENTION) {
                Text("Os dados permanecem disponíveis somente para consulta.")
            }
            DeliveryBlock("Quando", PartejarIcons.Clinical.calendar) {
                PartejarDateField(date, { date = it; dateError = null }, "Data do parto", !saving, dateError)
                PartejarTimeField(time, { time = it }, "Hora (opcional)", !saving)
            }
            DeliveryBlock("Onde", PartejarIcons.Navigation.workspace) {
                PartejarTextField(location, { location = it }, "Local do parto", enabled = !saving)
            }
            DeliveryBlock("Via de parto", PartejarIcons.Navigation.prontuario) {
                DeliveryOptions(GestanteDeliveryRoute.entries, route, !saving,
                    GestanteDeliveryRoute::deliveryLabel) { route = it }
            }
            DeliveryBlock("Desfecho materno", PartejarIcons.Navigation.gestantes) {
                DeliveryOptions(GestanteMaternalOutcome.entries, maternal, !saving,
                    GestanteMaternalOutcome::deliveryLabel) { maternal = it }
            }
            DeliveryBlock("Desfecho neonatal", PartejarIcons.Navigation.gestantes) {
                DeliveryOptions(GestanteNeonatalOutcome.entries, neonatal, !saving,
                    GestanteNeonatalOutcome::deliveryLabel) { neonatal = it }
            }
            DeliveryBlock("Observação", PartejarIcons.Actions.edit) {
                PartejarTextField(observation, { observation = it }, "Observação (opcional)", enabled = !saving,
                    singleLine = false, minLines = 3, maxLines = 6)
            }
            DeliveryBlock("Confirmação", PartejarIcons.Feedback.attention) {
                DeliveryConfirmation(confirmed, !saving) { confirmed = it }
            }
            DeliveryAction("Confirmar parto e encerrar", PartejarIcons.Feedback.success,
                enabled = !saving && confirmed, loading = saving) {
                val parsedDate = parseBrazilianDate(date)
                if (parsedDate == null) {
                    dateError = "Informe uma data válida (dd/MM/aaaa)."
                } else {
                    onSave(GestanteDeliveryInput(parsedDate.toString(), time.ifBlank { null }, route, location,
                        maternal, neonatal, observation.ifBlank { null }))
                }
            }
            DeliveryAction("Cancelar", PartejarIcons.Actions.back, !saving,
                variant = PartejarButtonVariant.SECONDARY, onClick = onCancel)
        }
    }
}

@Composable
private fun DeliveryHeader(icon: ImageVector, title: String, description: String) {
    val spacing = PartejarTheme.spacing
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(spacing.space12),
        verticalAlignment = Alignment.Top) {
        Icon(icon, null, Modifier.size(PartejarTheme.dimensions.iconLarge), tint = MaterialTheme.colorScheme.primary)
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
            Text(title, style = MaterialTheme.typography.titleLarge, modifier = Modifier.semantics { heading() })
            Text(description, style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun DeliveryBlock(title: String, icon: ImageVector, content: @Composable () -> Unit) {
    val spacing = PartejarTheme.spacing
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
        Row(horizontalArrangement = Arrangement.spacedBy(spacing.space8), verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.semantics { heading() }) {
            Icon(icon, null, Modifier.size(PartejarTheme.dimensions.iconSmall), tint = MaterialTheme.colorScheme.primary)
            Text(title, style = MaterialTheme.typography.titleMedium)
        }
        content()
        DeliveryDivider()
    }
}

@Composable
private fun <T> DeliveryOptions(
    options: Iterable<T>, selected: T, enabled: Boolean, label: (T) -> String, onSelected: (T) -> Unit
) {
    val spacing = PartejarTheme.spacing
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8),
        modifier = Modifier.semantics { selectableGroup() }) {
        options.forEach { option ->
            val isSelected = option == selected
            PartejarCard(onClick = { onSelected(option) }, enabled = enabled, motionEnabled = true,
                modifier = Modifier.fillMaxWidth().semantics { this.selected = isSelected }) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                    verticalAlignment = Alignment.CenterVertically) {
                    RadioButton(isSelected, null, enabled = enabled)
                    Text(label(option), Modifier.weight(1f), style = MaterialTheme.typography.bodyLarge,
                        color = if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface)
                }
            }
        }
    }
}

@Composable
private fun DeliveryConfirmation(checked: Boolean, enabled: Boolean, onCheckedChange: (Boolean) -> Unit) {
    val spacing = PartejarTheme.spacing
    PartejarCard(onClick = { onCheckedChange(!checked) }, enabled = enabled, motionEnabled = true,
        modifier = Modifier.fillMaxWidth().semantics {
            stateDescription = if (checked) "Encerramento confirmado" else "Encerramento não confirmado"
        }) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.Top) {
            Checkbox(checked, null, enabled = enabled)
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text("Confirmo o encerramento do acompanhamento", style = MaterialTheme.typography.titleSmall)
                Text("O parto será registrado e o acompanhamento passará para consulta.",
                    style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
    }
}

@Composable
internal fun DeliveryReadOnly(delivery: GestanteDelivery) {
    val spacing = PartejarTheme.spacing
    PartejarCard(Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
            DeliveryHeader(PartejarIcons.Feedback.success, "Parto registrado",
                "O acompanhamento clínico foi encerrado e permanece disponível para consulta.")
            DeliverySummaryGroup("Quando", PartejarIcons.Clinical.calendar) {
                DeliverySummaryValue("Data e hora",
                    listOfNotNull(formatBrazilianDate(delivery.date) ?: delivery.date, delivery.time).joinToString(" · "))
            }
            DeliverySummaryGroup("Onde", PartejarIcons.Navigation.workspace) {
                DeliverySummaryValue("Local do parto", delivery.location)
            }
            DeliverySummaryGroup("Via de parto", PartejarIcons.Navigation.prontuario) {
                DeliverySummaryValue("Via registrada", delivery.route.deliveryLabel())
            }
            DeliverySummaryGroup("Desfechos", PartejarIcons.Navigation.gestantes) {
                DeliverySummaryValue("Materno", delivery.maternalOutcome.deliveryLabel())
                DeliverySummaryValue("Neonatal", delivery.neonatalOutcome.deliveryLabel())
            }
            delivery.observation?.takeIf { it.isNotBlank() }?.let { observation ->
                DeliverySummaryGroup("Observação", PartejarIcons.Actions.edit) {
                    Text(observation, style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            }
            DeliverySummaryGroup("Registro", PartejarIcons.Navigation.profile, divider = false) {
                DeliverySummaryValue("Registrado por", delivery.registeredBy)
            }
        }
    }
}

@Composable
private fun DeliverySummaryGroup(title: String, icon: ImageVector, divider: Boolean = true,
    content: @Composable () -> Unit) {
    val spacing = PartejarTheme.spacing
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        Row(horizontalArrangement = Arrangement.spacedBy(spacing.space8), verticalAlignment = Alignment.CenterVertically) {
            Icon(icon, null, Modifier.size(PartejarTheme.dimensions.iconSmall), tint = MaterialTheme.colorScheme.primary)
            Text(title, style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        content()
        if (divider) DeliveryDivider()
    }
}

@Composable
private fun DeliverySummaryValue(label: String, value: String) {
    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4)) {
        Text(label, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        Text(value, style = MaterialTheme.typography.titleMedium)
    }
}

@Composable
private fun DeliveryAction(text: String, icon: ImageVector, enabled: Boolean, loading: Boolean = false,
    variant: PartejarButtonVariant = PartejarButtonVariant.PRIMARY, onClick: () -> Unit) {
    PartejarButton(text, onClick, enabled = enabled, isLoading = loading, variant = variant,
        motionEnabled = true, loadingDescription = "Salvando parto",
        leadingIcon = { Icon(icon, null, Modifier.size(PartejarTheme.dimensions.iconSmall)) })
}

@Composable
private fun DeliveryDivider() {
    HorizontalDivider(thickness = PartejarTheme.dimensions.strokeThin,
        color = MaterialTheme.colorScheme.outlineVariant)
}

/** One contained panel moves by one spacing token; no bounce, stagger or ambient motion. */
@Composable
private fun <T> DeliveryTransition(target: T, content: @Composable (T) -> Unit) {
    val reduced = reducedMotion()
    val offset = with(LocalDensity.current) { PartejarTheme.spacing.space12.roundToPx() }
    AnimatedContent(targetState = target, label = "deliveryPanel",
        contentKey = { if (it is DeliveryPanel.Summary) "summary" else it }, transitionSpec = {
        val enter = if (reduced) EnterTransition.None else
            fadeIn(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) +
                slideInVertically(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) { offset }
        val exit = if (reduced) ExitTransition.None else
            fadeOut(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) +
                slideOutVertically(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) { offset }
        (enter togetherWith exit).using(SizeTransform(clip = false) { _, _ ->
            if (reduced) snap() else tween(PartejarMotion.entrance, easing = PartejarMotion.calm)
        })
    }) { content(it) }
}

internal fun GestanteDeliveryRoute.deliveryLabel(): String = when (this) {
    GestanteDeliveryRoute.VAGINAL -> "Vaginal"
    GestanteDeliveryRoute.VAGINAL_INSTRUMENTAL -> "Vaginal instrumental"
    GestanteDeliveryRoute.CESAREAN -> "Cesárea"
    GestanteDeliveryRoute.OTHER -> "Outra via"
}

internal fun GestanteMaternalOutcome.deliveryLabel(): String = when (this) {
    GestanteMaternalOutcome.WITHOUT_RECORDED_COMPLICATION -> "Sem intercorrência registrada"
    GestanteMaternalOutcome.WITH_COMPLICATION -> "Com intercorrência"
    GestanteMaternalOutcome.OTHER -> "Outro"
}

internal fun GestanteNeonatalOutcome.deliveryLabel(): String = when (this) {
    GestanteNeonatalOutcome.LIVE_BIRTH -> "Nascido vivo"
    GestanteNeonatalOutcome.FETAL_NEONATAL_DEATH -> "Óbito fetal/neonatal"
    GestanteNeonatalOutcome.OTHER -> "Outro"
}
